import { addToast } from "@caprikorn/dynamo-ui";
import { FetchiosError } from "@caprikorn/dynamo-utils";
import {
  type FC,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { PageLoader } from "@components/PageLoader/PageLoader";
import { LS_KEYS } from "@core/core.constants";
import type { Group } from "@services/apiEcms/apiEcms.types";
import { useGetGroups } from "@services/apiEcms/useGetGroups";
import { usePostRefreshToken } from "@services/apiEcms/usePostRefreshToken";
import { GROUP_REFERENCE } from "@services/http";
import { GROUP_ID_LENGTH } from "@services/services.constants";
import { onLogout } from "@utils/auth.utils";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import type {
  GroupReferenceContextProps,
  GroupReferenceContextProviderProps,
} from "./GroupReferences.types";

export const GroupReferenceContext = createContext<GroupReferenceContextProps>({
  changeCurrentGroup: () => true,
  currentGroup: "",
});

const getValidInitialGroup = (groups: Group[] | undefined) => {
  const storedCurrentGroupRef =
    localStorage.getItem(LS_KEYS.CURRENT_GROUP_REF) || "";

  if (
    storedCurrentGroupRef.length === GROUP_ID_LENGTH ||
    groups?.some((g) => g.reference === storedCurrentGroupRef)
  ) {
    return storedCurrentGroupRef;
  }

  return groups?.[0]?.reference || "";
};

export const GroupReferenceProvider: FC<GroupReferenceContextProviderProps> = ({
  children,
}) => {
  const { t } = useCustomTranslation("common");
  const hasAttemptedRefresh = useRef(false);

  const {
    data: groups,
    isError: isGroupsError,
    error: groupsError,
    isPending: isGroupsPending,
    refetch: refetchGroups,
  } = useGetGroups();
  const [currentGroup, setCurrentGroup] = useState<string>(() =>
    getValidInitialGroup(groups),
  );
  const hasInitializedGroup = useRef(false);

  const {
    mutate: refreshAccessToken,
    isPending: isRefreshTokenPending,
    isError: isRefreshTokenError,
  } = usePostRefreshToken();

  // TODO missing useCallback, fix and test, it can cause regression
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const changeCurrentGroup = (newRef: string): void => {
    localStorage.setItem(LS_KEYS.CURRENT_GROUP_REF, newRef);
    setCurrentGroup(newRef);
    GROUP_REFERENCE.current = newRef;
  };

  const groupReferenceContextValues = useMemo(
    () => ({ changeCurrentGroup, currentGroup }),
    [currentGroup, changeCurrentGroup],
  );

  const isInvalidToken =
    isGroupsError &&
    groupsError instanceof FetchiosError &&
    groupsError.status === 401;

  useEffect(() => {
    if (groups && !hasInitializedGroup.current) {
      hasInitializedGroup.current = true;

      const validGroup = getValidInitialGroup(groups);
      setCurrentGroup(validGroup);
      GROUP_REFERENCE.current = validGroup;
    }
  }, [groups]);

  // The refresh token mechanism is made in this context because groups are fetched in all authenticated pages
  // and this WS responds 401 when token is invalid
  useEffect(() => {
    if (
      isInvalidToken &&
      !isRefreshTokenPending &&
      !isRefreshTokenError &&
      !hasAttemptedRefresh.current
    ) {
      hasAttemptedRefresh.current = true;

      refreshAccessToken(
        {
          username: localStorage.getItem(LS_KEYS.EMAIL),
          refreshToken: localStorage.getItem(LS_KEYS.REFRESH_TOKEN),
        },
        {
          onSuccess: ({ accessToken }) => {
            localStorage.setItem(LS_KEYS.ACCESS_TOKEN, accessToken);
            refetchGroups();
          },
          onError: () => {
            addToast({
              variant: "error",
              title: t("errors.login.title"),
              description: t("errors.login.description"),
            });

            onLogout();
          },
        },
      );
    }
  }, [
    t,
    isInvalidToken,
    isRefreshTokenPending,
    isRefreshTokenError,
    refreshAccessToken,
    refetchGroups,
  ]);

  if (isGroupsPending || isRefreshTokenPending) return <PageLoader />;

  return (
    <GroupReferenceContext.Provider value={groupReferenceContextValues}>
      {children}
    </GroupReferenceContext.Provider>
  );
};

export const useGroupReferences = (): GroupReferenceContextProps => {
  const context = useContext(GroupReferenceContext);

  if (!context) {
    throw new Error(
      "useGroupReferences must be used within a GroupReferencesProvider",
    );
  }

  return context;
};
